/**
 * phoneUtils.js — utilitários compartilhados de telefone BR.
 *
 * fev/2026: unificado (antes duplicado em entregasHelpers e loteHelpers).
 * - `formatTelBR(s)`: máscara de exibição enquanto o usuário digita
 * - `normalizePhoneBR(s)`: aplicado no `onBlur` — insere o "9" automaticamente
 *    quando detecta celular de 10 dígitos (formato pré-2012). Retorna só dígitos.
 * - `isValidPhoneBR(s)`: >= 10 dígitos (fixo 10, celular 11). Usado pela
 *    validação `getMissingRequiredFields` e pelo hint visual no input.
 */

/**
 * Máscara de exibição — não altera o número, só formata.
 * Ex.: "11999998888" → "(11) 99999-8888"
 *      "1133445566"  → "(11) 3344-5566"
 */
export const formatTelBR = (s) => {
  const d = String(s || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) {
    return d.replace(
      /^(\d{0,2})(\d{0,4})(\d{0,4}).*/,
      (_, a, b, c) => [a && `(${a}`, a && a.length === 2 && ") ", b, c && `-${c}`].filter(Boolean).join("").trim()
    );
  }
  return d.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
};

/**
 * Insere o dígito 9 quando detecta celular de 10 dígitos (formato antigo).
 * Retorna só dígitos — a UI reformata na exibição via `formatTelBR`.
 *
 * Regras:
 *   - 10 dígitos + 3º dígito (após DDD) em {6,7,8,9} → celular antigo, adiciona "9"
 *     Ex.: "1187654321" → "11987654321"
 *   - 10 dígitos + 3º dígito em {2,3,4,5} → fixo, mantém como está
 *   - 11+ dígitos → mantém como está
 *   - < 10 dígitos → deixa como está (validação vai bloquear no submit)
 */
export const normalizePhoneBR = (s) => {
  const d = String(s || "").replace(/\D/g, "").slice(0, 11);
  if (d.length === 10 && ["6", "7", "8", "9"].includes(d[2])) {
    return d.slice(0, 2) + "9" + d.slice(2);
  }
  return d;
};

/**
 * True se o número tem 10 dígitos (fixo) ou 11 dígitos (celular).
 */
export const isValidPhoneBR = (s) => {
  const d = String(s || "").replace(/\D/g, "");
  return d.length === 10 || d.length === 11;
};

/**
 * Contagem simples de dígitos.
 */
export const countPhoneDigits = (s) => String(s || "").replace(/\D/g, "").length;
